import { createContext, ReactNode, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { createToast } from "Utils/toastFunc";
import { PREBUDGETSTATUS } from "Constants/PreBudget";
import { usePreOrderDetails } from "./hooks/usePreOrderDetails";

type Product = {
  product_id: number;
  product_name: string;
  product_code: string;
  amount: number;
  product_price: number;
};

interface ItemsProviderProps {
  items: Product[];
  blockUserInteraction: boolean;
  deleteItem: (productId: number) => void;
  clearCart: () => void;
  deleteQuantityItem: (itemId: number) => void;
  addQuantityItem: (itemId: number) => void;
  addItem: (product: Product) => void;
  setBlockUserInteraction: (toogle: boolean) => void;
}

export const ItemsContext = createContext<ItemsProviderProps | null>(null);

export const ItemsProvider = ({ children }: { children: ReactNode }) => {
  const { id } = useParams<{ id: string }>();

  const preOderDetailsAPI = usePreOrderDetails(Number(id));
  const { products, budget_id, status } = preOderDetailsAPI.data ?? {};

  const [items, setItems] = useState<Product[]>([]);
  const [blockUserInteraction, setBlockUserInteraction] = useState(false);

  const clearCart = () => {
    setItems([]);
  };

  const deleteQuantityItem = (itemId: number) => {
    const itemsUpdated = items.flatMap((item) => {
      if (item.product_id === itemId) {
        if (item.amount === 1) return [];
        return {
          ...item,
          amount: item.amount - 1,
        };
      }
      return item;
    });

    setItems(itemsUpdated);
  };

  const deleteItem = (id: number) => {
    const productsStillInCart = items.filter((item) => item.product_id !== id);
    setItems(productsStillInCart);
  };

  const addQuantityItem = (itemId: number) => {
    const itemsUpdated = items.map((item) => {
      if (item.product_id === itemId) {
        return {
          ...item,
          amount: item.amount + 1,
        };
      }
      return item;
    });

    setItems(itemsUpdated);
  };

  const addItem = (product: Product) => {
    setItems((prev) => [...prev, product]);
  };

  useEffect(() => {
    const productsInPreBudget = products
      ? products?.map((product) => ({
          product_id: product.id,
          product_name: product.product,
          product_code: product.sku,
          amount: product.amount || 1,
          product_price: product.price ? Number(product.price) : 0,
        }))
      : [];

    setItems(productsInPreBudget);
  }, [preOderDetailsAPI.data]);

  useEffect(() => {
    if (status?.id === PREBUDGETSTATUS.canceled) {
      setBlockUserInteraction(true);

      return createToast(
        "error",
        "Não é possível editar este pré-orçamento, pois o mesmo foi cancelado."
      );
    }

    if (budget_id) {
      setBlockUserInteraction(true);
      return createToast(
        "error",
        "Não é possível editar este pré-orçamento, pois o mesmo já foi convertido em orçamento."
      );
    }
  }, [preOderDetailsAPI.data]);

  const values = useMemo(
    () => ({
      items,
      addItem,
      addQuantityItem,
      deleteQuantityItem,
      clearCart,
      deleteItem,
      blockUserInteraction,
      setBlockUserInteraction,
    }),
    [items, blockUserInteraction]
  );

  return (
    <ItemsContext.Provider value={values}>{children}</ItemsContext.Provider>
  );
};
